import { Purchases } from "../constants/Purchases";
import { ITpackage } from "../entities/product/types";
import UrlService from "../services/UrlService";
import TariffPackageService from "../services/TariffPackageService";
import Accessories from "../constants/Accessories";
import { getDeviceType, mapSubsidyContractByUrlParam } from "../utilites/util";
import Validator from "../validators/Validator";
import { ProductTypes } from "../constants/ProductTypes";
import Sections from "../constants/Sections";
import { ProductStatus } from "../constants/ProductStatus";
import Languages, { LanguageType } from "../constants/Languages";
import Config from "../config/config";
import PriceService from "../services/PriceService";


export default class BusinessRuleFacade {

  private static instance: BusinessRuleFacade;
  private product: any;
  private pageData: any;
  private urlParams: any;
  private readonly urlService: UrlService;

  private constructor() {}

  public static getInstance(): BusinessRuleFacade {
    if (!this.instance) {
      this.instance = new BusinessRuleFacade();
    }

    return this.instance;
  }

  public init(product: any, pageData: any) {
    this.product = product;
    this.pageData = pageData;
    this.urlParams = UrlService.getParamsFromUrlOnLoadPage();
  }

  public isAccessory(): boolean {
    return Accessories.includes(this.product?.productLine?.productLine);
  }

  public isTvOrCordlessPhoneOrStb(): boolean {
    const productLine = this.product?.productLine?.productLine || '';
    return [ProductTypes.TV, ProductTypes.STB, ProductTypes.CORDLESS_PHONE].includes(productLine);
  }

  public isComparable(): boolean {
    const productLine = this.product?.productLine?.productLine || '';
    const productTypesWithoutCompare = [ProductTypes.TV, ProductTypes.STB, ProductTypes.CORDLESS_PHONE];

    return !(productTypesWithoutCompare.includes(productLine));
  }

  public isSellSubsidyAllowed(): boolean {
    return this.product.sellSubsidy;
  }

  public isSellWithoutContractObligation(): boolean {
    return this.product.sellWithoutContractObligation;
  }

  public isSellCosmosAllowed(): boolean {
    return this.product.sellCosmos || this.product.sellTelenorHome;
  }

  public isSellTelenorHome(): boolean {
    return this.product.sellTelenorHome;
  }

  public isTpValid(): boolean {
    if (!this.isAccessory() && !this.isTvOrCordlessPhoneOrStb()) {
      const tariffPackage = TariffPackageService.getTpByTpId(this.product, Number(this.urlParams.tariff), UrlService.getPurchase());
      return typeof tariffPackage === 'object' && Object.keys(tariffPackage).length > 0;
    }

    return false;
  }

  public isColorIdValid(): boolean {
    const colorParam = this.urlParams.color;

    const color = this.product.productColorsImages?.find((item: any) => item.color.id === Number(colorParam));
    return typeof color !== 'undefined' && typeof color === 'object';
  }

  public isContractValid(): boolean {
    if (this.pageData?.sections?.subsidy?.contractSectionVisible && this.isSellSubsidyAllowed() && this.isTpValid()) {
      const tariffPackage = TariffPackageService.getTpByTpId(this.product, Number(this.urlParams.tariff), UrlService.getPurchase());
      const contract = tariffPackage.prices.subsidy.find((item: any) => item.installments === Number(mapSubsidyContractByUrlParam(this.urlParams.contract)));
      return typeof contract !== 'undefined' && typeof contract === 'object';
    }

    return false;
  }

  public isCosmosInstallmentsValid(): boolean {
    if (this.pageData?.sections?.cosmos?.cosmosInstallmentsSectionVisible && this.isSellCosmosAllowed() && this.isTpValid()) {
      const tariffPackage = TariffPackageService.getTpByTpId(this.product, Number(this.urlParams.tariff), UrlService.getPurchase());

      const cosmosInstallments = PriceService.getCosmosPriceByInstallments(tariffPackage?.prices?.cosmos);
      return typeof cosmosInstallments !== 'undefined' && typeof cosmosInstallments === 'object';
    }

    return false;
  }

  public isCosmosModelValid() {
    if (this.pageData?.sections?.cosmos?.cosmosModelSectionVisible && this.isSellCosmosAllowed()) {
      const cosmosModel = this.product.cosmosModel.find((item: any) => item.value === this.urlParams.cosmosModel);
      return typeof cosmosModel !== 'undefined' && typeof cosmosModel === 'object';
    }

    return false;
  }

  public isInstallmentsOverBillPeriodValid() {
    if (typeof this.urlParams.installments === 'undefined') {
      return false;
    }

    const urlInstallmentsParam = this.urlParams.installments === 'true';

    if (this.pageData?.sections?.subsidy?.installmentsSectionVisible && this.isTpValid()) {
      const tariffPackage = TariffPackageService.getTpByTpId(this.product, Number(this.urlParams.tariff), UrlService.getPurchase());
      const selectedItem = tariffPackage.prices.subsidy.find(
        (item: any) => item.installments === Number(mapSubsidyContractByUrlParam(this.urlParams.contract)) &&
          item.installmentsOverBillPeriod === urlInstallmentsParam
      );

      return typeof selectedItem !== 'undefined' ? selectedItem.installmentsOverBillPeriod : false;
    }

    return false;
  }

  public isCosmosParticipationValid() : boolean {
    if (UrlService.getPurchase() !== Purchases.COSMOS || !this.isSellCosmosAllowed() || !this.pageData?.sections?.cosmos?.participationSectionVisible) {
      return false;
    }

    if (Validator.isUndefined(this.urlParams.participation)) {
      return false;
    }

    if (!this.isCosmosParticipationExists()) {
      return false;
    }

    return true;
  }

  public isCosmosParticipationExists(): boolean {
    const cosmosPriceItem = PriceService.getCosmosPriceItem(this.product);
    return !Validator.isUndefined(cosmosPriceItem) && Validator.isObject(cosmosPriceItem) && !Validator.isUndefined(cosmosPriceItem?.cosmosParticipation);
  }

  public isCosmosParticipationLessThenMin(participationParam: number): boolean {
    if (this.isCosmosParticipationExists()) {
      return participationParam < Number(PriceService.getCosmosPriceItem(this.product)?.cosmosParticipation.min);
    }

    return false;
  }

  public getCosmosPriceBySelectedItem(tariffPackage: ITpackage): ITpackage | undefined {
    return tariffPackage.prices?.cosmos?.find((item: any) => item.selected);
  }

  public isCosmosParticipationGreaterThanMax(participationParam: number): boolean {
    if (this.isCosmosParticipationExists()) {
      return participationParam >= Number(PriceService.getCosmosPriceItem(this.product)?.cosmosParticipation.max);
    }

    return false;
  }

  public isCosmosParticipationValueInRange(participationParam: number):boolean {
    if (this.isCosmosParticipationExists()) {
      return participationParam >= Number(PriceService.getCosmosPriceItem(this.product)?.cosmosParticipation.min) && participationParam <= Number(PriceService.getCosmosPriceItem(this.product)?.cosmosParticipation.max) &&
        (participationParam % Number(PriceService.getCosmosPriceItem(this.product)?.cosmosParticipation.step) === 0);
    }

    return false;
  }


  public isBuyButtonApplicable(section: string, language: LanguageType): boolean {
    if (!Object.values(Sections).includes(section)) return false;
    if (!Object.values(Languages).includes(language)) return false;
    if (this.isAccessory() && !Config.isAccessoryVisible) return false;

    return  !this.isTvOrCordlessPhoneOrStb() &&
            section !== Sections.BUSINESS &&
            language !== Languages.EN &&
            this.product?.status !== ProductStatus.STATUS_NOT_FOR_SALE;
  }

  public isRedirectButtonApplicable(section: string, language: LanguageType): boolean {
    if (!Object.values(Sections).includes(section)) return false;
    if (!Object.values(Languages).includes(language)) return false;

    return  this.isTvOrCordlessPhoneOrStb() &&
            section !== Sections.BUSINESS &&
            language !== Languages.EN &&
            this.product?.status !== ProductStatus.STATUS_NOT_FOR_SALE;
  }

  public isApplicablePurchaseTypeFilter(section: string): boolean {
    if (!Object.values(Sections).includes(section)) return false;

    const productLine = getDeviceType();

    if (Config.isSubsidyOff && section === Sections.CONSUMER && (productLine === ProductTypes.PHONE || productLine === ProductTypes.TABLET)) {
      return false;
    }

    return true;
  }

}
